import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import SpeakerVoicesBulkActionBar from '@SiteContainers/SpeakerVoicesBulkActionBar'
import SpeakerVoicesTableHeader from '@SiteComponents/SpeakerVoicesTableHeader'
import SpeakerVoicesTableList from './SpeakerVoicesTableList'

import {
  openRemoveSpeakerVoicePopup,
} from '@SiteModules/Popup/actions'

import {
  fetchSpeakerVoices,
  createSpeakerVoice,
  setShouldCheckAutoFill,
  resetShouldCheckAutoFill,
  changeSelectedSpeakerVoices,
  setSearchTerm,
  setSelectedLanguagesNames,
  changeSelectedLanguageName,
} from '@SiteModules/SpeakerVoice/actions'

import {
  getCurrentWorkspaceId,
} from '@SiteModules/Workspace/selectors'

import {
  getSpeakerVoices,
  getHasLoadedInitially,
  getLoading,
  getTotalCount,
  getCurrentPage,
  getTotalPages,
  getShouldCheckAutoFill,
  getSelectedIds,
  getUsedLanguages,
  getLanguagesNames,
  getSelectedLanguagesNames,
} from '@SiteModules/SpeakerVoice/selectors'

import {
  getCurrentAccountAvailableTotalCredits,
  getCurrentAccountSubscriptionMaxSpeakerVoicesLimit,
} from '@SiteModules/Account/selectors'

import {
  ISpeakerVoice,
} from '@SiteModules/SpeakerVoice/types'

import './SpeakerVoicesTable.scss'

interface SpeakerVoicesTableProps {
  setCurrentSpeakerVoice(speakerVoice: ISpeakerVoice): void
}

const SpeakerVoicesTable: React.FC<SpeakerVoicesTableProps> = (props) => {
  const dispatch = useDispatch()

  const currentWorkspaceId = useSelector(getCurrentWorkspaceId)
  const totalCountOfSpeakerVoices = useSelector(getTotalCount)
  const speakerVoices = useSelector(getSpeakerVoices)
  const currentPage = useSelector(getCurrentPage)
  const totalPages = useSelector(getTotalPages)
  const hasLoadedInitially = useSelector(getHasLoadedInitially)
  const loading = useSelector(getLoading)
  const shouldCheckAutoFill = useSelector(getShouldCheckAutoFill)
  const availableTotalCredits = useSelector(getCurrentAccountAvailableTotalCredits)
  const maxSpeakerVoicesLimit = useSelector(getCurrentAccountSubscriptionMaxSpeakerVoicesLimit)
  const selectedSpeakerVoiceIds = useSelector(getSelectedIds)
  const usedLanguages = useSelector(getUsedLanguages)
  const languagesNames = useSelector(getLanguagesNames)
  const selectedLanguagesNames = useSelector(getSelectedLanguagesNames)

  const hasLoadedAllSpeakerVoices = hasLoadedInitially && speakerVoices.length === totalCountOfSpeakerVoices
  const hasSpeakerVoicesSelected = selectedSpeakerVoiceIds.length > 0

  useEffect(() => {
    const scrollContainer = document.querySelector('.SpeakerVoicesTableList__body')

    if (!scrollContainer) { return }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer
      const nearBottom = scrollHeight - scrollTop - clientHeight < window.Checksub.nearBottomDistance
      const hasSpeakerVoicesToLoad = speakerVoices.length < totalCountOfSpeakerVoices
      const hasPagesToLoad = currentPage < totalPages

      if (!loading && nearBottom && hasSpeakerVoicesToLoad && hasPagesToLoad) {
        dispatch(fetchSpeakerVoices({
          workspaceId: currentWorkspaceId,
          page: currentPage + 1
        }))
      }
    }

    const handleSetShouldCheckAutoFill = () => {
      dispatch(setShouldCheckAutoFill())
    }

    scrollContainer.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleSetShouldCheckAutoFill)

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleSetShouldCheckAutoFill)
    }
  }, [currentPage, totalPages, speakerVoices, totalCountOfSpeakerVoices, currentWorkspaceId, loading])

  useEffect(() => {
    // Handle the case when the first page of speakerVoices does not lead to a scrollable container because it does not fill the viewport. In this case repeated fetching is needed until the container becomes scrollable
    if (shouldCheckAutoFill) {
      const scrollContainer = document.querySelector('.SpeakerVoicesTableList__body')

      if (!scrollContainer || scrollContainer.scrollHeight === 0 || scrollContainer.clientHeight === 0) { return }

      const isScrollable = scrollContainer.scrollHeight > scrollContainer.clientHeight

      const hasElementsToLoad = speakerVoices.length < totalCountOfSpeakerVoices
      const hasPagesToLoad = currentPage < totalPages

      if (!loading && !isScrollable && hasElementsToLoad && hasPagesToLoad) {
        dispatch(fetchSpeakerVoices({
          workspaceId: currentWorkspaceId,
          page: currentPage + 1,
        }))
      } else {
        dispatch(resetShouldCheckAutoFill())
      }
    }
  }, [shouldCheckAutoFill, speakerVoices, totalCountOfSpeakerVoices, currentWorkspaceId, currentPage, totalPages, loading])

  const handleChangeSelectedSpeakerVoices = (e: React.ChangeEvent<HTMLInputElement>, speakerVoiceId: string) => {
    dispatch(changeSelectedSpeakerVoices({ speakerVoiceId, selectSpeakerVoice: e.target.checked }))
  }

  const handleOpenRemoveSpeakerVoicePopup = (speakerVoiceId: string) => {
    dispatch(openRemoveSpeakerVoicePopup({ speakerVoiceIds: [speakerVoiceId] }))
  }

  const handleSidePanelOpen = (speakerVoice: ISpeakerVoice) => {
    props.setCurrentSpeakerVoice(speakerVoice)
  }

  const createNewSpeakerVoiceAndOpenIt = () => {
    dispatch(createSpeakerVoice({
      workspaceId: currentWorkspaceId,
      openAfterCreation: true,
    }))
  }

  const handleSearch = (searchTerm?: string) => {
    dispatch(setSearchTerm({ searchTerm }))
    dispatch(fetchSpeakerVoices({ workspaceId: currentWorkspaceId, page: 1 }))
  }

  const handleChangeSelectedLanguageName = (e: React.ChangeEvent<HTMLInputElement>, languageName: string) => {
    dispatch(changeSelectedLanguageName({ languageName, select: e.target.checked }))
    dispatch(fetchSpeakerVoices({ workspaceId: currentWorkspaceId, page: 1 }))
  }

  const resetSelectedLanguagesNames = () => {
    dispatch(setSelectedLanguagesNames({ selectedLanguagesNames: [] }))
    dispatch(fetchSpeakerVoices({ workspaceId: currentWorkspaceId, page: 1 }))
  }

  const renderSpeakerVoiceTableMain = () => {
    return (
      <div className="SpeakerVoicesTable__main">
        <SpeakerVoicesTableHeader
          currentWorkspaceId={currentWorkspaceId}
          totalCountOfSpeakerVoices={totalCountOfSpeakerVoices}
          availableTotalCredits={availableTotalCredits}
          maxSpeakerVoicesLimit={maxSpeakerVoicesLimit}
          createNewSpeakerVoiceAndOpenIt={createNewSpeakerVoiceAndOpenIt}
          speakerVoicesLoading={loading}
          handleSearch={handleSearch}
          usedLanguages={usedLanguages}
          languagesNames={languagesNames}
          selectedLanguagesNames={selectedLanguagesNames}
          changeSelectedLanguageNames={handleChangeSelectedLanguageName}
          resetSelectedLanguagesNames={resetSelectedLanguagesNames}
        />
        <SpeakerVoicesTableList
          speakerVoices={speakerVoices}
          changeSelectedSpeakerVoices={handleChangeSelectedSpeakerVoices}
          selectedSpeakerVoiceIds={selectedSpeakerVoiceIds}
          openRemoveSpeakerVoicePopup={handleOpenRemoveSpeakerVoicePopup}
          loading={loading}
          hasLoadedAllSpeakerVoices={hasLoadedAllSpeakerVoices}
          handleSidePanelOpen={handleSidePanelOpen}
        />
      </div>
    )
  }

  return (
    <div className="SpeakerVoicesTable">
      {renderSpeakerVoiceTableMain()}
      {hasSpeakerVoicesSelected && (
        <SpeakerVoicesBulkActionBar />
      )}
    </div>
  )
}

export default SpeakerVoicesTable
